import type { VoidFunctionComponent } from 'react';
import type { Column, SortingRule } from 'react-table';

import Locator from '../../../../shared/Locator';
import { useParams } from '../../../contexts/RouterContext';
import { useSnippets } from '../../../hooks/api/snippets';
import { IconAndNode, icons } from '../../generic';
import SimpleReactTable from '../../generic/SimpleReactTable';
import TableHeader from '../../generic/TableHeader';
import type { Snippet } from '../../../../shared/snippets';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import ConfidenceBadge from './ConfidenceBadge';
import LicenseIssueCounts from './LicenseIssueCounts';
import SnippetsTreeView from './SnippetsTreeView';

import styles from './Snippets.module.less';

const defaultSortMode: SortingRule[] = [{
  id: 'package',
  desc: false,
}];

const snippetKindIcons = {
  snippet: icons.receipt,
  file: icons.fileOutline,
  dependency: icons.box,
};

const columns: Array<Column<Snippet>> = [
  {
    id: 'package',
    Header: <TableHeader title="Package" />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      // Snippets don't contain the raw package name so we need to parse it out of the Locator.
      const packageName = Locator.parse(snippet.id)?.package ?? 'Unknown';
      const icon = snippetKindIcons[snippet.kind];
      return (
        <div>
          <IconAndNode icon={icon} marginAmount="0.5em">
            <span>{packageName}</span>
          </IconAndNode>
        </div>
      );
    },
    resizable: false,
    sortable: false,
    // Ensure the text doesn't overflow and instead truncates with an ellipsis.
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'version',
    Header: <TableHeader title="Version" />,
    //  Snippets don't contain the raw package version, we need to parse them out of a Locator
    accessor: (row: Snippet) => {
      return Locator.parse(row.id)?.revision ?? '?.?.?';
    },
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'issues',
    Header: <TableHeader title="License Issues" />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      return <LicenseIssueCounts issueCounts={snippet.issueCounts.licensing} />;
    },
    resizable: false,
    sortable: false,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'confidence',
    Header: <TableHeader title="Conf." />,
    Cell: ({ original: snippet }: { original: Snippet }) => {
      return <ConfidenceBadge confidence={snippet.confidence} />;
    },
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'matches',
    Header: <TableHeader title="Matches" />,
    accessor: (row: Snippet) => row.matchIds.length,
    resizable: false,
    sortable: false,
    // width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
];

/**
 * SnippetsContainer - Route container component for the Snippet Scanning page within a project.
 */
const SnippetsContainer: VoidFunctionComponent = () => {
  //  Note: this will change (and probably be moved) but for now this pulls the dummy data so we can see it.
  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;
  const { data: snippets, isLoading: isLoadingSnippets } = useSnippets(userRevisionLocator);

  return (
    <>
      <h1 className={styles.heading}>
        Snippets
      </h1>
      <div className={styles.layout}>
        <SnippetsTreeView userRevisionLocator={userRevisionLocator} />
        <div className={styles.table}>
          <SimpleReactTable
            data={snippets?.results ?? []}
            columns={columns}
            isLoading={isLoadingSnippets}
            defaultSortMode={defaultSortMode}
            minRows={10}
            //  TODO: this will likely change in the future.
            isSortable={false}
          />
        </div>
      </div>
    </>
  );
};

export default SnippetsContainer;
